import React from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import ChatBox from "@/components/ChatBox";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { TICKET_STATUS, ROLE_LABELS } from "@/lib/constants";
import { TicketBadge } from "@/components/support/TicketList";
import { Mail, Phone, UserRound, MessageSquareDashed } from "lucide-react";
import { RateTicket, TicketRating } from "@/components/support/RateTicket";

export function TicketThread({ ticket, meId, staff, onChanged }) {
  if (!ticket) {
    return (
      <div className="h-full min-h-[420px] flex flex-col items-center justify-center text-muted-foreground gap-2 text-sm" data-testid="ticket-placeholder">
        <MessageSquareDashed className="w-10 h-10 opacity-40" /> اختر تذكرة لعرض المحادثة
      </div>
    );
  }
  const setStatus = async (status) => {
    try {
      await api.patch(`/support/tickets/${ticket.id}/status`, { status });
      toast.success("تم تحديث حالة التذكرة");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  return (
    <div className="p-4 sm:p-5 flex flex-col gap-4" data-testid="ticket-thread">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs text-muted-foreground">تذكرة #{ticket.number}</div>
          <h2 className="font-heading font-bold text-lg" data-testid="ticket-subject">{ticket.subject}</h2>
          {ticket.assigned_name && <div className="text-xs text-muted-foreground mt-0.5">الموظف المسؤول: {ticket.assigned_name}</div>}
        </div>
        {staff ? (
          <Select value={ticket.status} onValueChange={setStatus} dir="rtl">
            <SelectTrigger className="w-40" data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(TICKET_STATUS).map(([k, v]) => (
                <SelectItem key={k} value={k} data-testid={`ticket-status-option-${k}`}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className="flex items-center gap-2">
            <TicketBadge status={ticket.status} />
            {ticket.status !== "closed" && (
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => setStatus("closed")} data-testid="ticket-close-btn">إغلاق التذكرة</Button>
            )}
          </div>
        )}
      </div>
      {staff && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground bg-muted/40 rounded-xl p-3" data-testid="ticket-user-info">
          <span className="flex items-center gap-1"><UserRound className="w-3.5 h-3.5" />{ticket.user_name} · {ROLE_LABELS[ticket.user_role]}</span>
          {ticket.user_email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{ticket.user_email}</span>}
          {ticket.user_phone && <span className="flex items-center gap-1" dir="ltr"><Phone className="w-3.5 h-3.5" />{ticket.user_phone}</span>}
        </div>
      )}
      <TicketRating ticket={ticket} />
      {!staff && ticket.status === "closed" && !ticket.rating && <RateTicket ticket={ticket} onRated={onChanged} />}
      {!staff && ticket.status === "closed" && (
        <div className="text-xs text-muted-foreground" data-testid="ticket-closed-note">هذه التذكرة مغلقة — إرسال رسالة جديدة سيعيد فتحها.</div>
      )}
      <ChatBox key={ticket.id} threadType="support" refId={ticket.id} meId={meId} heightClass="h-[60vh] lg:h-[26rem]" onActivity={onChanged} quickReplyName={staff ? ticket.user_name : undefined} />
    </div>
  );
}
